// interfaces
import {
    BOOLEAN_AS_STRING,
    MARKETPLACE_BUSINESS_LINE,
    OrderData,
    OrderApiData,
    DEFAULT_DINEOUT_CONFIRM_ORDER_STATUS_LIST,
    VALID_WALLET_TYPES,
    PAYMENT_CODES,
} from "@swiggy-private/payment-types";
import {
    PlaceOrderParams,
    PlaceOrderRequestParams,
    PlaceOrderPaymentMethodMeta,
    SwiggyMoneyOrderPlaceParams,
    DashOrderResponse,
} from "@payment-client/interfaces/OrderPublic";
import { CREDIT_TYPE } from "@payment-client/interfaces/CardApi";

// utils
import { isAndroidPlatform, isiOSPlatform } from "@payment-client/utils/Headers";
import { isEmpty } from "@swiggy-private/js-utils";

// constants
import {
    DEFAULT_CONFIRM_ORDER_STATUS_LIST,
    DEFAULT_MIN_ANDROID_VERSION_FOR_PAYMENT_PENDING_FLOW,
    DEFAULT_MIN_ANDROID_VERSION_FOR_PAYMENT_PENDING_FLOW_PUBLIC_API,
    DEFAULT_MIN_IOS_VERSION_FOR_PAYMENT_PENDING_FLOW,
    DEFAULT_MIN_IOS_VERSION_FOR_PAYMENT_PENDING_FLOW_PUBLIC_API,
} from "@payment-client/constants/Order";
import { Response } from "@swiggy-private/checkout-core";
import { DOWNSTREAM_HOSTS, PAYMENT_CONFIG } from "@payment-client/constants/Hosts";

export const SWIGGY_MONEY_CREDIT_METHOD = "SwiggyMoney";

export const isPaymentMethodAmazonPay = (paymentMethod: string): boolean => {
    return (
        VALID_WALLET_TYPES.AMAZONPAY === paymentMethod.replace("-SSO", "").toUpperCase() ||
        VALID_WALLET_TYPES.AMAZONPAYWEB === paymentMethod.replace("-SSO", "").toUpperCase()
    );
};

export const isPaymentMethodAmazonPayLater = (paymentMethod: string): boolean => {
    return paymentMethod.toUpperCase() === PAYMENT_CODES.AMAZONPAYLATER.toUpperCase();
};

/**
 * Maps the `payment_method_meta` produced by `useOnPay` (and `getPlaceOrderConfigParams`) into
 * the contract expected by Swiggy Super place-order for Amazon Pay (APB) and Amazon Pay Later (APL):
 *
 *   payment_method_meta: {
 *       amazon_pay_redirect_url: string,
 *       amazon_pay_meta: string,
 *       is_enough_balance_in_wallet: boolean,
 *   }
 *
 * For non-Amazon payment methods the payload is returned as-is (same reference).
 */
export const mapPaymentMethodMetaForSwiggySuper = <T extends { paymentMethod?: string; [key: string]: unknown }>(
    payload: T,
    paymentReturnUrl?: string,
): T => {
    const paymentMethod = payload.paymentMethod;
    if (
        !paymentMethod ||
        (!isPaymentMethodAmazonPay(paymentMethod) && !isPaymentMethodAmazonPayLater(paymentMethod))
    ) {
        return payload;
    }

    const inMeta = (payload.payment_method_meta as Record<string, unknown> | undefined) ?? {};

    /* eslint-disable @typescript-eslint/naming-convention */
    const mappedMeta: PlaceOrderPaymentMethodMeta = {
        amazon_pay_redirect_url:
            (inMeta.amazon_pay_redirect_url as string | undefined) ??
            paymentReturnUrl ??
            DOWNSTREAM_HOSTS.PAYMENT_RETURN_URL,
        amazon_pay_meta: (inMeta.amazon_payment_meta_data as string | undefined) ?? "",
        is_enough_balance_in_wallet: inMeta.isEnoughBalanceInWallet === BOOLEAN_AS_STRING.TRUE,
    };
    /* eslint-enable @typescript-eslint/naming-convention */

    return {
        ...payload,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        payment_method_meta: mappedMeta,
    };
};

export const isPaymentMethodCard = (paymentMethod: string): boolean => {
    return paymentMethod.toUpperCase() === "JUSPAY";
};

export const isCardOrNetBankingPayment = (paymentMethod: string): boolean => {
    return isPaymentMethodCard(paymentMethod) || paymentMethod.toUpperCase() === "JUSPAY-NB";
};

export const isPaymentMethodPhonePe = (paymentMethod: string): boolean => {
    return VALID_WALLET_TYPES.PHONEPE === paymentMethod.replace("-SSO", "").toUpperCase();
};

export const isPaymentmethodEMI = (paymentMethod: string): boolean => {
    return paymentMethod.toLowerCase() === "third-party-emi";
};

export const isPaymentmethodMMT = (paymentMethod: string): boolean => {
    return paymentMethod === PAYMENT_CODES.MMT;
};

export const getPlaceOrderConfigParams = (
    params: PlaceOrderRequestParams,
): {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    payment_method_meta?: PlaceOrderPaymentMethodMeta;
} => {
    const { paymentMethod, useJuspayNative: isJuspayNative, paymentReturnUrl } = params;
    const configParams: ReturnType<typeof getPlaceOrderConfigParams> = {};

    // if payment method doesn't exist
    if (!paymentMethod) {
        return configParams;
    }

    // if payment method is phonepe, set callback url in payment method meta
    if (isPaymentMethodPhonePe(paymentMethod)) {
        configParams.payment_method_meta = {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            phonepe_web_callback_url: paymentReturnUrl || DOWNSTREAM_HOSTS.PAYMENT_RETURN_URL,
        };
    }

    // if payment method is amazon, set callback url in payment method meta
    if (isPaymentMethodAmazonPay(paymentMethod)) {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        configParams.payment_method_meta = {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            amazon_pay_redirect_url: paymentReturnUrl || DOWNSTREAM_HOSTS.PAYMENT_RETURN_URL,
            ...((params?.payment_method_meta as PlaceOrderPaymentMethodMeta) || {}),
        };
    }

    if (isCardOrNetBankingPayment(paymentMethod)) {
        /* eslint-disable @typescript-eslint/naming-convention */
        configParams.payment_method_meta = {
            is_juspay_native: String(!!isJuspayNative),
        };

        if (isPaymentMethodCard(paymentMethod)) {
            configParams.payment_method_meta["cvv_mandatory"] = params?.cvv_mandatory ?? true;
        }

        if (!isJuspayNative) {
            configParams.payment_method_meta["juspay_redirect_url"] =
                paymentReturnUrl || DOWNSTREAM_HOSTS.PAYMENT_RETURN_URL;
        }
        /* eslint-enable */
    }

    // if payment method is emi, set callback url in payment method meta
    if (isPaymentmethodEMI(paymentMethod)) {
        /* eslint-disable @typescript-eslint/naming-convention */
        configParams.payment_method_meta = {
            juspay_redirect_url: paymentReturnUrl || DOWNSTREAM_HOSTS.PAYMENT_RETURN_URL,
        };
    }

    const isMMTSplitPay =
        params?.selected_split_pay_amounts != null &&
        typeof params.selected_split_pay_amounts === "object" &&
        PAYMENT_CODES.MMT in (params.selected_split_pay_amounts as Record<string, unknown>);

    if (!isEmpty(params?.expenseCodeData) && (isPaymentmethodMMT(paymentMethod) || isMMTSplitPay)) {
        configParams.payment_method_meta = {
            ...configParams.payment_method_meta,
            mmt_meta: {
                expense_code: params?.expenseCodeData?.projectCode ?? "",
                purpose_of_ordering: params?.expenseCodeData?.purposeOfOrdering ?? "",
                ...(params?.expenseCodeData?.tripTags && { tripTags: params.expenseCodeData.tripTags }),
                ...(params?.expenseCodeData?.groupOrderDetails && { groupOrderDetails: params.expenseCodeData.groupOrderDetails }),
            },
        };
    }

    return configParams;
};

export const getSwiggyMoneyOrderPlaceParams = (
    data: PlaceOrderParams,
    paymentReturnUrl: string,
): SwiggyMoneyOrderPlaceParams => {
    const amount = Number(data.paymentAmount) || 0;
    let swiggyMoneyOrderPlaceParamsData: SwiggyMoneyOrderPlaceParams;

    swiggyMoneyOrderPlaceParamsData = {
        amount,
        creditMethod: SWIGGY_MONEY_CREDIT_METHOD,
        creditType: CREDIT_TYPE.SELFLOAD,
        paymentInformation: [
            {
                amount,
                paymentMethod: data.payment_cod_method,
                paymentMethodMeta: {
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    payment_netbanking_type: data.payment_netbanking_type,
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    card_bin_number: data.card_bin_number,
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    upi_transaction_info: data.upi_transaction_info,
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    juspay_redirect_url: paymentReturnUrl,
                },
            },
        ],
    };

    // TODO - Cleanup required after feature
    if (data.giftDetails) {
        swiggyMoneyOrderPlaceParamsData = {
            ...swiggyMoneyOrderPlaceParamsData,
            amount: Number(data.giftDetails.originalAmount),
            receiverMeta: data.giftDetails.receiverMeta,
            creditType: CREDIT_TYPE.VOUCHER,
        };
    }

    return swiggyMoneyOrderPlaceParamsData;
};

export function getOrderTxnIdFromOrderData(data: OrderData): {
    orderId?: string;
    transactionId?: string;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    order_id?: string;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    transaction_id?: string;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    payment_transaction_id?: string;
} {
    const transactionId = data.targetedMarketplacePaymentReferenceId ?? data.paymentTxnId;

    return {
        orderId: data.orderId != null ? String(data.orderId) : undefined,
        transactionId,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        order_id: data.orderId != null ? String(data.orderId) : undefined,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        transaction_id: transactionId ?? "",
        // eslint-disable-next-line @typescript-eslint/naming-convention
        payment_transaction_id: transactionId ?? "",
    };
}

export function isEligibleForNewPendingPaymentFlow(platform?: string, versionCode?: string): boolean {
    if (isEmpty(platform) || isEmpty(versionCode) || isNaN(Number(versionCode))) {
        return false;
    }
    if (isAndroidPlatform(platform as string)) {
        const minAndroidVersion =
            PAYMENT_CONFIG.MIN_ANDROID_VERSION_FOR_PAYMENT_PENDING_FLOW ||
            DEFAULT_MIN_ANDROID_VERSION_FOR_PAYMENT_PENDING_FLOW;
        return Number(versionCode) >= Number(minAndroidVersion);
    }
    if (isiOSPlatform(platform as string)) {
        const minIosVersion =
            PAYMENT_CONFIG.MIN_IOS_VERSION_FOR_PAYMENT_PENDING_FLOW || DEFAULT_MIN_IOS_VERSION_FOR_PAYMENT_PENDING_FLOW;
        return Number(versionCode) >= Number(minIosVersion);
    }
    return false;
}

export function isEligibleForPublicApi(platform?: string, versionCode?: string): boolean {
    if (isEmpty(platform) || isEmpty(versionCode) || isNaN(Number(versionCode))) {
        return false;
    }
    if (isAndroidPlatform(platform as string)) {
        const minAndroidVersion =
            PAYMENT_CONFIG.MIN_ANDROID_VERSION_FOR_PAYMENT_PENDING_FLOW_PUBLIC_API ||
            DEFAULT_MIN_ANDROID_VERSION_FOR_PAYMENT_PENDING_FLOW_PUBLIC_API;
        return Number(versionCode) >= Number(minAndroidVersion);
    }
    if (isiOSPlatform(platform as string)) {
        const minIosVersion =
            PAYMENT_CONFIG.MIN_IOS_VERSION_FOR_PAYMENT_PENDING_FLOW_PUBLIC_API ||
            DEFAULT_MIN_IOS_VERSION_FOR_PAYMENT_PENDING_FLOW_PUBLIC_API;
        return Number(versionCode) >= Number(minIosVersion);
    }
    return false;
}

export function isOrderDataExistsInResponse(
    response: Response<DashOrderResponse | OrderApiData>,
): response is Response<OrderApiData> {
    return response.data != null && "orderData" in response.data;
}

export function isOrderConfirmed(businessline: string, orderStatus?: string): boolean {
    const confirmOrderStatusList = getConfirmOrderStatusList(businessline);

    return orderStatus != null && confirmOrderStatusList.indexOf(orderStatus) !== -1;
}

export function getConfirmOrderStatusList(businessline: string): string[] {
    let defaultStatusList = DEFAULT_CONFIRM_ORDER_STATUS_LIST;

    try {
        switch (businessline) {
            case MARKETPLACE_BUSINESS_LINE.DINEOUT:
                defaultStatusList = DEFAULT_DINEOUT_CONFIRM_ORDER_STATUS_LIST;
                return defaultStatusList;

            default:
                return defaultStatusList;
        }
    } catch (err) {
        console.error(err);
        return defaultStatusList;
    }
}
